import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Legend,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { businessMonthKey, businessToday, formatCurrency } from '@/lib/format';

// Series colours are validated for both themes (light #fcfcfb / dark #0d0d0d) — do not substitute.
const DEPOSITED_COLOR = '#059669';
const REFUNDED_COLOR = '#dc2626';
const ASSIGNED_COLOR = '#1f5fae';
const UNASSIGNED_COLOR = '#5b9ce8';

const TOOLTIP_STYLE = { backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' };

const formatCompact = (amount: number) => {
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
    return amount.toFixed(0);
};

interface DepositTransactionRow {
    id: number;
    contract_id: number | null;
    transactions: { amount: number | null; currency: string | null; transaction_at: string | null } | null;
}

interface RefundTransactionRow {
    id: number;
    transactions: { amount: number | null; transaction_at: string | null } | null;
}

interface PoolDepositRow {
    id: number;
    transactions: { amount: number | null } | null;
    contract_deposit_refunds: { id: number }[] | null;
}

interface MonthBucket {
    deposited: number;
    refunded: number;
    assigned: number;
    unassigned: number;
}

export default function ContractDepositsAnalytics() {
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });

    const fromDate = format(monthRange.from, 'yyyy-MM-dd');
    const toDate = format(monthRange.to, 'yyyy-MM-dd');

    const { data: summaryData, isLoading } = useQuery({
        queryKey: ['contract-deposits-analytics', fromDate, toDate],
        queryFn: async () => {
            // Deposits are dated by the bank date on the transaction, not by when the row was created.
            const { data: deposits, error } = await supabase
                .from('contract_deposits')
                .select('id, contract_id, transactions!inner(amount, currency, transaction_at)')
                .gte('transactions.transaction_at', fromDate)
                .lte('transactions.transaction_at', toDate + 'T23:59:59');
            if (error) throw error;

            // Errors throw like the deposits call above: an RLS or permissions failure must
            // surface, not render as "Total Refunded 0".
            const refundResult = await supabase
                .from('contract_deposit_refunds')
                .select('id, transactions!inner(amount, transaction_at)')
                .gte('transactions.transaction_at', fromDate)
                .lte('transactions.transaction_at', toDate + 'T23:59:59');
            if (refundResult.error) throw refundResult.error;

            const depositRows = (deposits ?? []) as DepositTransactionRow[];
            const refundRows = (refundResult.data ?? []) as RefundTransactionRow[];

            const monthlyMap = new Map<string, MonthBucket>();
            const current = new Date(monthRange.from.getFullYear(), monthRange.from.getMonth(), 1);
            while (current <= monthRange.to) {
                const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
                monthlyMap.set(key, { deposited: 0, refunded: 0, assigned: 0, unassigned: 0 });
                current.setMonth(current.getMonth() + 1);
            }

            // The stored Ghana month. `transaction_at` is a timestamptz, so `new Date(…)` read
            // through local getters moves every deposit and refund within the viewer's offset of a
            // month boundary into the neighbouring bar.
            const bucketFor = (transactionAt: string | null | undefined) => {
                const key = businessMonthKey(transactionAt);
                return key ? monthlyMap.get(key) : undefined;
            };

            let totalDeposited = 0;
            let assignedCount = 0;

            depositRows.forEach(d => {
                const amount = d.transactions?.amount || 0;
                totalDeposited += amount;
                if (d.contract_id != null) assignedCount += 1;
                const bucket = bucketFor(d.transactions?.transaction_at);
                if (!bucket) return;
                bucket.deposited += amount;
                if (d.contract_id != null) bucket.assigned += 1;
                else bucket.unassigned += 1;
            });

            let totalRefunded = 0;

            refundRows.forEach(r => {
                const amount = r.transactions?.amount || 0;
                totalRefunded += amount;
                const bucket = bucketFor(r.transactions?.transaction_at);
                if (!bucket) return;
                bucket.refunded += amount;
            });

            const monthlyData = Array.from(monthlyMap.entries()).map(([key, bucket]) => {
                const [year, month] = key.split('-');
                return {
                    month: format(new Date(Number(year), Number(month) - 1, 1), 'MMM yy'),
                    ...bucket,
                };
            });

            const depositCount = depositRows.length;

            return {
                totalDeposited,
                totalRefunded,
                netHeld: totalDeposited - totalRefunded,
                depositCount,
                refundCount: refundRows.length,
                assignedCount,
                avgDeposit: depositCount > 0 ? totalDeposited / depositCount : 0,
                monthlyData,
            };
        },
    });

    // All-time queue of deposits still waiting to be handled: no contract AND no refunds —
    // the same semantics as the Records tab's default "Unassigned" filter.
    const { data: poolData, isLoading: poolLoading } = useQuery({
        queryKey: ['contract-deposits-unassigned-pool'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('contract_deposits')
                .select('id, transactions!inner(amount), contract_deposit_refunds(id)')
                .is('contract_id', null);
            if (error) throw error;

            const rows = ((data ?? []) as PoolDepositRow[]).filter(
                r => (r.contract_deposit_refunds?.length || 0) === 0
            );

            return {
                count: rows.length,
                total: rows.reduce((sum, r) => sum + (r.transactions?.amount || 0), 0),
            };
        },
    });

    if (isLoading || poolLoading) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-5">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28" />)}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                    {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-80" />)}
                </div>
            </div>
        );
    }

    const totalDeposited = summaryData?.totalDeposited || 0;
    const totalRefunded = summaryData?.totalRefunded || 0;
    const netHeld = summaryData?.netHeld || 0;
    const depositCount = summaryData?.depositCount || 0;
    const avgDeposit = summaryData?.avgDeposit || 0;
    const refundCount = summaryData?.refundCount || 0;
    const assignedCount = summaryData?.assignedCount || 0;
    const poolCount = poolData?.count || 0;
    const poolTotal = poolData?.total || 0;

    return (
        <div className="space-y-6">
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            {/* Stats Row */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Total Deposited"
                    value={<CurrencyDisplay amount={totalDeposited} />}
                    description="The gross value of deposits received from drivers in the selected period, counted on the date the money reached the bank rather than the date the record was created."
                    subtitle={`${depositCount} deposit${depositCount === 1 ? '' : 's'} received`}
                />
                <StatCard
                    title="Total Refunded"
                    value={<CurrencyDisplay amount={totalRefunded} />}
                    description="The value of deposit money paid back to drivers in the period. Each refund is a DEBIT transaction assigned to the deposit it reverses, and a deposit can be refunded partly or in full."
                    subtitle={`${refundCount} refund${refundCount === 1 ? '' : 's'} recorded`}
                />
                <StatCard
                    title="Net Held"
                    value={
                        <span className={netHeld < 0 ? 'text-red-600' : undefined}>
                            {/* CurrencyDisplay renders the magnitude — sign a negative net explicitly */}
                            {netHeld < 0 && '−'}
                            <CurrencyDisplay amount={netHeld} />
                        </span>
                    }
                    description="Total deposited minus total refunded — the deposit money the business still holds from the period's transactions. This is the figure that nets against a driver's total paid on their contract. Refunds count in the period they were paid, not the period of the deposit they reverse, so this can be negative."
                />
                <StatCard
                    title="Deposits"
                    value={depositCount.toLocaleString()}
                    description="How many deposits were recorded in the period, and what the typical one is worth. A rising average usually means larger vehicles or bigger upfront commitments."
                    subtitle={`Avg ${formatCurrency(avgDeposit)} · ${assignedCount} assigned`}
                />
                <StatCard
                    title="Unassigned Pool"
                    value={<CurrencyDisplay amount={poolTotal} />}
                    description="Deposit money sitting against no contract and with no refund recorded — the queue the Records tab opens on. This figure is all-time and does not move with the selected month range."
                    subtitle={`${poolCount} deposit${poolCount === 1 ? '' : 's'} awaiting a contract`}
                />
            </div>

            {/* Charts Row - Side by Side */}
            <div className="grid gap-4 md:grid-cols-2">
                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Deposits vs Refunds</CardTitle>
                        <CardDescription>Money in against money returned, by the month it moved</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <ComposedChart data={summaryData?.monthlyData} barGap={2}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="month" className="text-xs" />
                                <YAxis className="text-xs" tickFormatter={(v) => formatCompact(v)} />
                                <RechartsTooltip
                                    formatter={(value: number) => formatCurrency(value)}
                                    contentStyle={TOOLTIP_STYLE}
                                />
                                <Legend />
                                <Bar dataKey="deposited" fill={DEPOSITED_COLOR} name="Deposited" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="refunded" fill={REFUNDED_COLOR} name="Refunded" radius={[4, 4, 0, 0]} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                <Card className="border border-border/40">
                    <CardHeader>
                        <CardTitle>Assignment Mix</CardTitle>
                        <CardDescription>Deposits recorded each month, split by whether they are linked to a contract</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={summaryData?.monthlyData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="month" className="text-xs" />
                                <YAxis className="text-xs" allowDecimals={false} />
                                <RechartsTooltip
                                    formatter={(value: number) => `${value} deposit${value === 1 ? '' : 's'}`}
                                    contentStyle={TOOLTIP_STYLE}
                                />
                                <Legend />
                                <Bar dataKey="assigned" stackId="deposits" fill={ASSIGNED_COLOR} name="Assigned" />
                                <Bar dataKey="unassigned" stackId="deposits" fill={UNASSIGNED_COLOR} name="Unassigned" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
